import React, { useState } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism, vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import glsl from 'react-syntax-highlighter/dist/esm/languages/prism/glsl';
import c from 'react-syntax-highlighter/dist/esm/languages/prism/c';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup'; // HTML/XML
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import php from 'react-syntax-highlighter/dist/esm/languages/prism/php';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import { Copy, Check } from 'lucide-react';

// Register optimized languages
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('py', python);
SyntaxHighlighter.registerLanguage('c', c);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('html', markup);
SyntaxHighlighter.registerLanguage('xml', markup);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('glsl', glsl);
SyntaxHighlighter.registerLanguage('php', php);
SyntaxHighlighter.registerLanguage('go', go);

interface CodeBlockProps {
    language?: string;
    code: string;
    className?: string;
    variant?: 'dark' | 'light';
    showLanguage?: boolean;
    showCopy?: boolean;
}

const CodeBlock: React.FC<CodeBlockProps> = ({
    language = 'javascript',
    code,
    className = '',
    variant = 'dark',
    showLanguage = true,
    showCopy = true
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const isLight = variant === 'light';

    return (
        <div className={`my-8 rounded-xl overflow-hidden border ${isLight ? 'bg-white border-gray-200' : 'bg-[#1e1e1e] border-[#3e3e3e]'} shadow-sm transition-all duration-200 hover:shadow-md ${className}`}>
            {(showLanguage || showCopy) && (
                <div className={`flex justify-between items-center px-4 py-0.5 border-b ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-[#2d2d2d] border-[#3e3e3e]'}`}>
                    {showLanguage && (
                        <span className={`text-[0.7rem] font-bold tracking-widest uppercase font-brand ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
                            {language === 'js' ? 'javascript' : language}
                        </span>
                    )}
                    {showCopy && (
                        <button
                            className={`flex items-center gap-2 p-1.5 rounded-md transition-colors ${copied ? 'text-green-500' : isLight ? 'text-gray-400 hover:bg-gray-200 hover:text-gray-700' : 'text-gray-400 hover:bg-white/10 hover:text-white'}`}
                            onClick={handleCopy}
                            aria-label={copied ? "Code copied to clipboard" : "Copy code to clipboard"}
                        >
                            {copied ? (
                                <Check size={16} strokeWidth={2.5} />
                            ) : (
                                <Copy size={16} strokeWidth={2.5} />
                            )}
                            <span className="text-[0.75rem] font-medium">{copied ? 'Copied' : 'Copy'}</span>
                        </button>
                    )}
                </div>
            )}
            <div className="relative">
                <SyntaxHighlighter
                    language={language.toLowerCase()}
                    wrapLongLines={true}
                    style={isLight ? prism : vscDarkPlus}
                    customStyle={{
                        margin: 0,
                        padding: '1.5rem',
                        fontSize: '0.9rem',
                        lineHeight: '1.6',
                        background: 'transparent',
                        fontFamily: "'Roboto Mono', 'Fira Code', monospace",
                    }}
                >
                    {code}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};

export default CodeBlock;
